import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, of, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

@Injectable()
export class ErrorRetryInterceptor implements HttpInterceptor {
  minRetryDelay = 500;
  maxRetryDelay = 3000;
  retryMaxAttempts = 2;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(this.retryAfterDelay());
  }

  retryAfterDelay(): MonoTypeOperatorFunction<HttpEvent<unknown>> {
    return retryWhen(errors => {
      return errors.pipe(
        mergeMap((err, count) => {
          // throw error when we've retried ${retryMaxAttempts} number of times and still get an error
          if (!(err instanceof HttpErrorResponse)) return throwError(() => new Error(err));

          const isAssetRequest = err.url?.includes('https://assets.');
          const maxRetries = isAssetRequest ? 1 : this.retryMaxAttempts;

          if (count >= maxRetries) return throwError(() => err);

          if (err?.error?.code) {
            // The idea here is that if there is no code, then we don't know what the error is and we should retry, but if there is a code, then we can check if it's a rate limit error and retry if it is
            const code = err.error.code;
            const message = err.error.message;
            if (code !== 'server_exception' || !message?.includes('rate limit exceeded')) return throwError(() => err);
          }
          if ((err.status === 404 || err.status === 422) && !isAssetRequest) {
            return throwError(() => err);
          }

          const retryDelay = Math.floor(Math.random() * this.maxRetryDelay - this.minRetryDelay + this.minRetryDelay);
          return of(err).pipe(
            mergeMap(() => {
              if (isAssetRequest && err.status === 500) {
                console.log(`Retrying ${err.url} once after 10 seconds`);
                // mysterious 500 errors from the asset server are usually fixed by retrying. Cloudfront caches the error responses for 10 seconds, so we need to wait at least that long before retrying
                return timer(10_000);
              }
              console.log(`Retrying ${err.url}. Retry count ${count + 1}`);
              return timer(retryDelay);
            })
          );
        })
      );
    });
  }
}
